import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { logAudit } from "@/lib/audit";

export interface NurseryConfig {
  id: string;
  nursery_id: string;
  earliest_session_start: string;
  latest_session_end: string;
  earliest_shift_start: string;
  latest_shift_end: string;
  days_of_operation: string[];
  operations_weekend_enabled: boolean;
}

export function useNurseryConfig() {
  const { user } = useAuth();
  return useQuery<NurseryConfig | null>({
    queryKey: ["nursery_config"],
    enabled: !!user,
    queryFn: async () => {
      // First get nursery_id
      const { data: profile } = await supabase
        .from("profiles")
        .select("nursery_id")
        .eq("user_id", user!.id)
        .maybeSingle();
      if (!profile?.nursery_id) return null;

      const { data, error } = await supabase
        .from("nursery_config")
        .select("*")
        .eq("nursery_id", profile.nursery_id)
        .maybeSingle();
      if (error) throw error;
      return data as NurseryConfig | null;
    },
    staleTime: 60_000,
  });
}

export function useActiveZones() {
  const { user } = useAuth();
  return useQuery<string[]>({
    queryKey: ["active_zones"],
    enabled: !!user,
    queryFn: async () => {
      const { data: profile } = await supabase
        .from("profiles")
        .select("nursery_id")
        .eq("user_id", user!.id)
        .maybeSingle();
      if (!profile?.nursery_id) return [];

      const { data } = await supabase
        .from("nurseries")
        .select("active_zones")
        .eq("id", profile.nursery_id)
        .single();
      return (data?.active_zones as string[]) || [];
    },
    staleTime: 60_000,
  });
}

export function useSaveNurseryConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (config: Partial<NurseryConfig> & { nursery_id: string }) => {
      const { data: existing } = await supabase
        .from("nursery_config")
        .select("id")
        .eq("nursery_id", config.nursery_id)
        .maybeSingle();

      if (existing) {
        const { error } = await supabase
          .from("nursery_config")
          .update(config)
          .eq("nursery_id", config.nursery_id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("nursery_config")
          .insert(config);
        if (error) throw error;
      }
      const fields = Object.keys(config).filter((k) => k !== "nursery_id").join(", ");
      logAudit({ description: `Updated nursery operating hours/days (${fields})`, recordType: "nursery_config", recordId: config.nursery_id });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["nursery_config"] });
    },
  });
}

export function useSaveActiveZones() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ nurseryId, zones }: { nurseryId: string; zones: string[] }) => {
      const { error } = await supabase
        .from("nurseries")
        .update({ active_zones: zones })
        .eq("id", nurseryId);
      if (error) throw error;
      logAudit({ description: `Updated active zones: ${zones.join(", ") || "none"}`, recordType: "nursery", recordId: nurseryId });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["active_zones"] });
      qc.invalidateQueries({ queryKey: ["zones"] });
    },
  });
}

export function useSetZoneActive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ zoneId, isActive }: { zoneId: string; isActive: boolean }) => {
      const { data, error } = await supabase.rpc("set_zone_active", {
        p_zone_id: zoneId,
        p_is_active: isActive,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["zones"] });
      qc.invalidateQueries({ queryKey: ["active_zones"] });
    },
  });
}
